import { RefreshCcw } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiError } from "@/lib/api";
import { useDataManagementViewModel } from "@/pages/dashboard/admin/useDataManagementViewModel";

export function DataManagementView() {
  const {
    costCodesData,
    syncCostCodesMutation,
    syncCustomersMutation,
    syncTeamsMutation,
    syncTimeclockFromQBMutation,
    syncFieldNotesFromQBMutation,
    syncFieldNotesToQBMutation,
    syncTimeclockToQBMutation,
  } = useDataManagementViewModel();
  return (
    <div className="mt-4 grid grid-cols-1 gap-4 [&>*]:min-w-0">
                <Card>
                  <CardHeader>
                    <CardTitle>Data Management</CardTitle>
                    <CardDescription>Sync cost codes, customers, teams, punches, and field notes from Quickbase.</CardDescription>
                  </CardHeader>
                  <CardContent className="grid gap-3">
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => syncCostCodesMutation.mutate()}
                        disabled={syncCostCodesMutation.isPending}
                      >
                        <RefreshCcw className="mr-2 size-4" />
                        {syncCostCodesMutation.isPending ? "Syncing..." : "Sync Cost Codes"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => syncCustomersMutation.mutate()}
                        disabled={syncCustomersMutation.isPending}
                      >
                        <RefreshCcw className="mr-2 size-4" />
                        {syncCustomersMutation.isPending ? "Syncing..." : "Sync Customers"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => syncTeamsMutation.mutate()}
                        disabled={syncTeamsMutation.isPending}
                      >
                        <RefreshCcw className="mr-2 size-4" />
                        {syncTeamsMutation.isPending ? "Syncing..." : "Sync Teams"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => syncTimeclockFromQBMutation.mutate()}
                        disabled={syncTimeclockFromQBMutation.isPending}
                      >
                        <RefreshCcw className="mr-2 size-4" />
                        {syncTimeclockFromQBMutation.isPending ? "Syncing..." : "Sync Punches from QB"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => syncTimeclockToQBMutation.mutate()}
                        disabled={syncTimeclockToQBMutation.isPending}
                      >
                        <RefreshCcw className="mr-2 size-4" />
                        {syncTimeclockToQBMutation.isPending ? "Syncing..." : "Sync Punches to QB"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => syncFieldNotesFromQBMutation.mutate()}
                        disabled={syncFieldNotesFromQBMutation.isPending}
                      >
                        <RefreshCcw className="mr-2 size-4" />
                        {syncFieldNotesFromQBMutation.isPending ? "Syncing..." : "Sync Field Notes from QB"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => syncFieldNotesToQBMutation.mutate()}
                        disabled={syncFieldNotesToQBMutation.isPending}
                        title="Pushes notes with no QuickBase rid, and re-pushes acks that have not synced yet"
                      >
                        <RefreshCcw className="mr-2 size-4" />
                        {syncFieldNotesToQBMutation.isPending ? "Syncing..." : "Sync Field Notes to QB"}
                      </Button>
                    </div>
                    {syncCostCodesMutation.isSuccess && (
                      <div className="text-xs text-muted-foreground">
                        Cost codes synced: {syncCostCodesMutation.data?.created} created, {syncCostCodesMutation.data?.updated} updated.
                      </div>
                    )}
                    {syncCustomersMutation.isSuccess && (
                      <div className="text-xs text-muted-foreground">
                        Customers synced: {syncCustomersMutation.data?.created} created, {syncCustomersMutation.data?.updated} updated.
                      </div>
                    )}
                    {syncTeamsMutation.isSuccess && (
                      <div className="text-xs text-muted-foreground">
                        Teams synced: {syncTeamsMutation.data?.teams_upserted} teams, {syncTeamsMutation.data?.managers_linked} manager links, {syncTeamsMutation.data?.users_updated} users updated.
                      </div>
                    )}
                    {syncTimeclockFromQBMutation.isSuccess && (
                      <div className="text-xs text-muted-foreground">
                        Punches synced: {syncTimeclockFromQBMutation.data?.created} created, {syncTimeclockFromQBMutation.data?.updated} updated, {syncTimeclockFromQBMutation.data?.skipped} skipped.
                      </div>
                    )}
                    {syncTimeclockFromQBMutation.isError && (
                      <div className="text-xs text-destructive">
                        {syncTimeclockFromQBMutation.error instanceof ApiError
                          ? syncTimeclockFromQBMutation.error.message
                          : "Punch sync failed."}
                      </div>
                    )}
                    {syncFieldNotesFromQBMutation.isSuccess && (
                      <div className="text-xs text-muted-foreground">
                        Field notes pulled: {syncFieldNotesFromQBMutation.data?.created} created,{" "}
                        {syncFieldNotesFromQBMutation.data?.updated} updated,{" "}
                        {syncFieldNotesFromQBMutation.data?.skipped} skipped
                        {(syncFieldNotesFromQBMutation.data?.no_user ?? 0) > 0
                          ? `, ${syncFieldNotesFromQBMutation.data?.no_user} no matching user`
                          : ""}
                        .
                      </div>
                    )}
                    {syncFieldNotesFromQBMutation.isError && (
                      <div className="text-xs text-destructive">
                        {syncFieldNotesFromQBMutation.error instanceof ApiError
                          ? syncFieldNotesFromQBMutation.error.message
                          : "Field notes pull failed."}
                      </div>
                    )}
                    {syncFieldNotesToQBMutation.isSuccess && (
                      <div className="text-xs text-muted-foreground">
                        Field notes pushed to QB: {syncFieldNotesToQBMutation.data?.synced} synced,{" "}
                        {syncFieldNotesToQBMutation.data?.failed} failed
                        {" "}(new notes without a rid + pending ack updates only).
                      </div>
                    )}
                    {syncFieldNotesToQBMutation.isError && (
                      <div className="text-xs text-destructive">
                        {syncFieldNotesToQBMutation.error instanceof ApiError
                          ? syncFieldNotesToQBMutation.error.message
                          : "Field notes push failed."}
                      </div>
                    )}
                    {syncTimeclockToQBMutation.isSuccess && (
                      <div className="text-xs text-muted-foreground">
                        Leftover punches pushed to QB: {syncTimeclockToQBMutation.data?.processed} processed, {syncTimeclockToQBMutation.data?.synced} synced, {syncTimeclockToQBMutation.data?.failed} failed
                        {" "}(only rows with no qb_record_id).
                      </div>
                    )}
                    {syncTimeclockToQBMutation.isError && (
                      <div className="text-xs text-destructive">
                        {syncTimeclockToQBMutation.error instanceof ApiError
                          ? syncTimeclockToQBMutation.error.message
                          : "Push to QB failed."}
                      </div>
                    )}
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                      {(costCodesData ?? []).map((cc) => (
                        <div key={cc.id} className="flex items-center justify-between rounded-lg border p-3">
                          <span className="text-sm font-medium">{cc.cost_code}</span>
                          <Badge variant="outline">{cc.active ? "Active" : "Inactive"}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
    </div>
  );
}
